/**
 * Profile Achievements Tab
 * Shows user achievements with progress
 */

import { useState, useMemo } from 'react'
import {
  Box,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  LinearProgress,
  Divider,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  CircularProgress,
  Alert,
} from '@mui/material'
import type { SelectChangeEvent } from '@mui/material'
import {
  Close,
  EmojiEvents,
  Hiking,
  Groups,
  Explore,
  Public,
  Timer,
  Star,
  LocalFireDepartment,
  LocationOn,
  Event,
} from '@mui/icons-material'
import type { DisplayAchievement } from '@api/types/achievements'
import {
  useSystemAchievements,
  useSystemAchievementsProgress,
  useCollectionAchievementsProgress,
  useUserSystemAchievements,
} from '../hooks/useAchievements'
import {
  getCriteriaCategory,
  getCriteriaIconName,
  getCriteriaIconColor,
  isStreakAchievement,
  getStreakUnitLabel,
  formatProgress,
  getRemainingCount,
  type AchievementIconName,
} from '../utils/achievementUtils'
import { getImageUrl } from '@/utils/imageUtils'

// ===================
// ICON COMPONENT
// ===================

interface AchievementIconProps {
  iconName: AchievementIconName
  color: string
  size?: number
}

function AchievementIcon({ iconName, color, size = 40 }: AchievementIconProps) {
  const sx = { fontSize: size, color }

  switch (iconName) {
    case 'Hiking':
      return <Hiking sx={sx} />
    case 'Explore':
      return <Explore sx={sx} />
    case 'LocationOn':
      return <LocationOn sx={sx} />
    case 'Event':
      return <Event sx={sx} />
    case 'Groups':
      return <Groups sx={sx} />
    case 'LocalFireDepartment':
      return <LocalFireDepartment sx={sx} />
    case 'Timer':
      return <Timer sx={sx} />
    case 'Star':
      return <Star sx={sx} />
    case 'EmojiEvents':
      return <EmojiEvents sx={sx} />
    case 'Public':
    default:
      return <Public sx={sx} />
  }
}

// ===================
// COMPONENT
// ===================

interface ProfileAchievementsTabProps {
  userId?: string
  isOwnProfile?: boolean
}

type AchievementSortOption = 'date' | 'progress' | 'name' | 'category'

export function ProfileAchievementsTab({
  userId,
  isOwnProfile = true,
}: ProfileAchievementsTabProps) {
  const [selectedAchievement, setSelectedAchievement] = useState<DisplayAchievement | null>(null)
  const [achievementSort, setAchievementSort] = useState<AchievementSortOption>('date')

  // ===== OWN PROFILE DATA =====
  // Fetch system achievements (own profile only)
  const {
    data: ownSystemAchievements,
    isLoading: isLoadingOwnSystem,
    error: ownSystemError,
  } = useSystemAchievements()

  // Fetch progress data for streak info (own profile only)
  const { data: systemProgress } = useSystemAchievementsProgress()

  // Fetch collection achievements (own profile only)
  const {
    data: ownCollectionAchievements,
    isLoading: isLoadingOwnCollection,
  } = useCollectionAchievementsProgress()

  // ===== OTHER USER PROFILE DATA =====
  // Fetch unified achievements for other users
  const {
    data: otherUserAchievements,
    isLoading: isLoadingOtherAchievements,
    error: otherUserError,
  } = useUserSystemAchievements(isOwnProfile ? undefined : userId)

  // Determine loading/error state
  const isLoading = isOwnProfile
    ? isLoadingOwnSystem || isLoadingOwnCollection
    : isLoadingOtherAchievements
  const error = isOwnProfile ? ownSystemError : otherUserError

  // Build progress lookup for streak data (own profile only)
  const progressLookup = useMemo(() => {
    if (!systemProgress) return new Map()
    return new Map(systemProgress.map((p) => [p.achievementId, p]))
  }, [systemProgress])

  // Transform API data to display format
  const displayAchievements = useMemo((): DisplayAchievement[] => {
    const achievements: DisplayAchievement[] = []

    if (isOwnProfile) {
      // Own profile: Use separate system and collection achievement data

      // Add system achievements
      if (ownSystemAchievements) {
        for (const sa of ownSystemAchievements) {
          const progress = progressLookup.get(sa.id)
          achievements.push({
            id: sa.id,
            name: sa.name,
            shortDescription: sa.shortDescription,
            fullDescription: sa.fullDescription,
            iconUrl: sa.iconUrl,
            category: getCriteriaCategory(sa.criteriaType),
            criteriaType: sa.criteriaType,
            currentValue: sa.currentValue,
            threshold: sa.threshold,
            completionPercentage: sa.completionPercentage,
            isCompleted: sa.isCompleted,
            completedDate: sa.completedDate,
            currentStreak: progress?.currentStreak,
            bestStreak: progress?.bestStreak,
            lastStreakActivityDate: progress?.lastStreakActivityDate,
            sourceType: 'system',
          })
        }
      }

      // Add collection achievements
      if (ownCollectionAchievements) {
        for (const ca of ownCollectionAchievements) {
          achievements.push({
            id: ca.achievementId,
            name: ca.achievementName,
            shortDescription: ca.description || ca.achievementName,
            fullDescription: ca.description,
            iconUrl: ca.iconUrl,
            category: ca.collectionName || 'Collection',
            criteriaType: null,
            currentValue: ca.completedCount,
            threshold: ca.totalAdventureCount,
            completionPercentage: ca.completionPercentage,
            isCompleted: ca.isCompleted,
            completedDate: ca.completedDate,
            sourceType: 'collection',
          })
        }
      }
    } else {
      // Other user's profile: Use unified achievements endpoint
      if (otherUserAchievements) {
        for (const ua of otherUserAchievements) {
          achievements.push({
            id: ua.achievementId,
            name: ua.achievementName,
            shortDescription: ua.description || ua.achievementName,
            fullDescription: ua.description,
            iconUrl: ua.iconUrl,
            category: 'Achievement',
            criteriaType: null,
            currentValue: ua.completedCount,
            threshold: ua.totalAdventureCount,
            completionPercentage: ua.completionPercentage,
            isCompleted: ua.isCompleted,
            completedDate: ua.completedDate,
            sourceType: 'collection',
          })
        }
      }
    }

    return achievements
  }, [isOwnProfile, ownSystemAchievements, ownCollectionAchievements, otherUserAchievements, progressLookup])

  // Sort achievements based on selected option
  const sortedAchievements = useMemo(() => {
    const sorted = [...displayAchievements]

    switch (achievementSort) {
      case 'date':
        // Sort by completion date (completed first, most recent first), then incomplete by progress
        return sorted.sort((a, b) => {
          // Both completed - sort by earned date (most recent first)
          if (a.isCompleted && b.isCompleted) {
            const dateA = a.completedDate ? new Date(a.completedDate).getTime() : 0
            const dateB = b.completedDate ? new Date(b.completedDate).getTime() : 0
            return dateB - dateA
          }
          // Completed comes before incomplete
          if (a.isCompleted && !b.isCompleted) return -1
          if (!a.isCompleted && b.isCompleted) return 1
          // Both incomplete - sort by progress
          return b.completionPercentage - a.completionPercentage
        })

      case 'progress':
        // Sort by progress percentage (highest first)
        return sorted.sort((a, b) => b.completionPercentage - a.completionPercentage)

      case 'name':
        // Sort alphabetically by name
        return sorted.sort((a, b) => a.name.localeCompare(b.name))

      case 'category':
        // Sort by category, then by name within category
        return sorted.sort((a, b) => {
          const categoryCompare = a.category.localeCompare(b.category)
          if (categoryCompare !== 0) return categoryCompare
          return a.name.localeCompare(b.name)
        })

      default:
        return sorted
    }
  }, [displayAchievements, achievementSort])

  const handleSortChange = (event: SelectChangeEvent) => {
    setAchievementSort(event.target.value as AchievementSortOption)
  }

  // Loading state
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  // Error state
  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load achievements. Please try again later.
      </Alert>
    )
  }

  return (
    <Box>
      {/* Achievements Section */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Achievements
          </Typography>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="achievement-sort-label">Sort by</InputLabel>
            <Select
              labelId="achievement-sort-label"
              value={achievementSort}
              label="Sort by"
              onChange={handleSortChange}
            >
              <MenuItem value="date">Date</MenuItem>
              <MenuItem value="progress">Progress</MenuItem>
              <MenuItem value="name">Name</MenuItem>
              <MenuItem value="category">Category</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {sortedAchievements.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <EmojiEvents sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography color="text.secondary">
              No achievements yet. Start exploring to earn achievements!
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {sortedAchievements.map((achievement) => {
              const iconName = getCriteriaIconName(achievement.criteriaType)
              const iconColor = getCriteriaIconColor(achievement.criteriaType)
              const isStreak = achievement.criteriaType !== null && isStreakAchievement(achievement.criteriaType)

              return (
                <Box
                  key={achievement.id}
                  onClick={() => setSelectedAchievement(achievement)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 1.5,
                    borderRadius: 1,
                    cursor: 'pointer',
                    border: '1px solid',
                    borderColor: achievement.isCompleted ? 'success.light' : 'divider',
                    backgroundColor: achievement.isCompleted ? 'success.50' : 'transparent',
                    opacity: achievement.isCompleted ? 1 : 0.85,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: achievement.isCompleted ? 'success.100' : 'action.hover',
                      transform: 'translateX(4px)',
                    },
                  }}
                >
                  {/* Icon - 64x64 */}
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      minWidth: 64,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: 1,
                      backgroundColor: achievement.isCompleted ? 'background.paper' : 'action.hover',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    {achievement.iconUrl ? (
                      <Box
                        component="img"
                        src={getImageUrl(achievement.iconUrl)!}
                        alt={achievement.name}
                        sx={{ width: 48, height: 48, objectFit: 'contain' }}
                      />
                    ) : (
                      <AchievementIcon iconName={iconName} color={iconColor} />
                    )}
                  </Box>

                  {/* Text content */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {/* Row 1: Short description + completion date */}
                    <Box sx={{ height: 32, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 500,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          flex: 1,
                        }}
                      >
                        {achievement.shortDescription}
                      </Typography>
                      {/* Completion date for completed achievements */}
                      {achievement.isCompleted && achievement.completedDate && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'success.main',
                            fontWeight: 500,
                            ml: 2,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {new Date(achievement.completedDate).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })}
                        </Typography>
                      )}
                    </Box>

                    {/* Row 2: Progress bar + value */}
                    <Box sx={{ height: 32, display: 'flex', alignItems: 'center', gap: 2 }}>
                      <LinearProgress
                        variant="determinate"
                        value={achievement.completionPercentage}
                        sx={{
                          flex: 1,
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: 'action.hover',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: achievement.isCompleted ? 'success.main' : 'primary.main',
                            borderRadius: 4,
                          },
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 'bold',
                          minWidth: 70,
                          textAlign: 'right',
                          color: achievement.isCompleted ? 'success.main' : 'text.secondary',
                        }}
                      >
                        {formatProgress(achievement.currentValue, achievement.threshold)}
                      </Typography>
                    </Box>

                    {/* Row 3: Streak info (only for streak achievements) */}
                    {isStreak && !achievement.isCompleted && achievement.currentStreak !== undefined && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          <LocalFireDepartment sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5, color: '#ff5722' }} />
                          {achievement.currentStreak} {getStreakUnitLabel(achievement.criteriaType!, achievement.currentStreak)} streak
                        </Typography>
                        {achievement.bestStreak !== undefined && achievement.bestStreak > 0 && (
                          <Typography variant="caption" color="text.secondary">
                            Best: {achievement.bestStreak}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
              )
            })}
          </Box>
        )}
      </Paper>

      {/* Achievement Detail Dialog */}
      <Dialog
        open={!!selectedAchievement}
        onClose={() => setSelectedAchievement(null)}
        maxWidth="sm"
        fullWidth
      >
        {selectedAchievement && (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2, pr: 6 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 1,
                  backgroundColor: 'action.hover',
                }}
              >
                {selectedAchievement.iconUrl ? (
                  <Box
                    component="img"
                    src={getImageUrl(selectedAchievement.iconUrl)!}
                    alt={selectedAchievement.name}
                    sx={{ width: 40, height: 40, objectFit: 'contain' }}
                  />
                ) : (
                  <AchievementIcon
                    iconName={getCriteriaIconName(selectedAchievement.criteriaType)}
                    color={getCriteriaIconColor(selectedAchievement.criteriaType)}
                    size={32}
                  />
                )}
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  {selectedAchievement.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {selectedAchievement.category}
                </Typography>
              </Box>
              <IconButton
                onClick={() => setSelectedAchievement(null)}
                sx={{ position: 'absolute', right: 8, top: 8 }}
              >
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              {selectedAchievement.fullDescription ? (
                <Typography
                  variant="body1"
                  paragraph
                  dangerouslySetInnerHTML={{ __html: selectedAchievement.fullDescription }}
                />
              ) : (
                <Typography variant="body1" paragraph>
                  {selectedAchievement.shortDescription}
                </Typography>
              )}

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Progress:
                </Typography>
                <Box sx={{ flex: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={selectedAchievement.completionPercentage}
                    sx={{
                      height: 10,
                      borderRadius: 5,
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: selectedAchievement.isCompleted ? 'success.main' : 'primary.main',
                      },
                    }}
                  />
                </Box>
                <Typography variant="body2" fontWeight="bold">
                  {formatProgress(selectedAchievement.currentValue, selectedAchievement.threshold)}
                </Typography>
              </Box>

              {/* Streak details */}
              {selectedAchievement.criteriaType !== null &&
                isStreakAchievement(selectedAchievement.criteriaType) && (
                  <Box sx={{ mt: 2, p: 2, borderRadius: 1, backgroundColor: 'action.hover' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <LocalFireDepartment sx={{ color: '#ff5722' }} />
                      <Typography variant="body2" fontWeight="bold">
                        Streak Details
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {selectedAchievement.currentStreak !== undefined && (
                        <Typography variant="body2" color="text.secondary">
                          Current streak: <strong>{selectedAchievement.currentStreak} {getStreakUnitLabel(selectedAchievement.criteriaType, selectedAchievement.currentStreak)}</strong>
                        </Typography>
                      )}
                      {selectedAchievement.bestStreak !== undefined && selectedAchievement.bestStreak > 0 && (
                        <Typography variant="body2" color="text.secondary">
                          Best streak: <strong>{selectedAchievement.bestStreak} {getStreakUnitLabel(selectedAchievement.criteriaType, selectedAchievement.bestStreak)}</strong>
                        </Typography>
                      )}
                      {!selectedAchievement.isCompleted && (
                        <Typography variant="body2" color="text.secondary">
                          Remaining: <strong>{getRemainingCount(selectedAchievement.currentValue, selectedAchievement.threshold)} more {getStreakUnitLabel(selectedAchievement.criteriaType, getRemainingCount(selectedAchievement.currentValue, selectedAchievement.threshold))} to complete</strong>
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}

              {selectedAchievement.isCompleted && selectedAchievement.completedDate && (
                <Typography variant="body2" color="success.main" sx={{ mt: 2 }}>
                  Completed on {new Date(selectedAchievement.completedDate).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </Typography>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedAchievement(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  )
}
